<template>
  <div class="vx-container vx-mx-auto vx-px-3 vx-pb-32 md:vx-px-8 2xl:vx-max-w-screen-xl">
      <page-header v-if="newResourceObj && shouldShowNew">Find a New Number</page-header>
      <page-header v-else-if="editResourceObj && shouldShowEdit">Update Number</page-header>
      <page-header v-else-if="showResourceObj && shouldShowShow">Show Phone Number {{ showResourceObj.phone_number }}</page-header>
      <page-header v-else>Phone Numbers</page-header>

      <phone-number-provision v-if="newResourceObj && shouldShowNew" :phoneNumber="newResourceObj"></phone-number-provision>

      <phone-number-form v-if="editResourceObj && shouldShowEdit" :phoneNumber="editResourceObj"></phone-number-form>

      <phone-number-show v-if="showResourceObj && shouldShowShow" :phoneNumber="showResourceObj"></phone-number-show>

      <div v-if="shouldShowList" class="list">

          <table class="c-table resource-table">
              <caption class="c-table__title">
                      <vx-button-group v-if="isImpersonatingOrDeveloper" size="md" class="pull-right">
                        <vx-button
                            @click.prevent="presentNewPhoneNumberUi"
                            class="vx-flex-grow lg:vx-flex-grow-0"
                            type="button"
                        >
                            <span class="vx-text-sm">Add Phone Number</span>
                        </vx-button>
                        <vx-dropdown placement="right">
                            <vx-button type="button" size="md" class="!vx-rounded-l-none">
                                <font-awesome-icon :icon="faChevronDown" class="vx-text-sm"/>
                            </vx-button>

                            <template v-slot:items>
                                <vx-dropdown-item @click="showHostNumberModal = true">
                                    <font-awesome-icon :icon="faPlus" class="vx-text-sm"></font-awesome-icon>
                                    <span class="vx-text-sm">Add Hosted Number</span>
                                </vx-dropdown-item>
                            </template>
                        </vx-dropdown>
                    </vx-button-group>

                    <search
                        v-if="!loadingCollection"
                        placeholder="Filter"
                        identity="phone_numbers-search"
                        method="get"
                        maxlength="191"
                        :searchUrl="searchUrl">
                    </search>

                    <host-phone-number-modal v-model:visible="showHostNumberModal" @save="onHostNumberSave"></host-phone-number-modal>

                  </caption>

              <thead v-if="!loadingCollection" class="c-table__head c-table__head--slim">
                  <tr class="c-table__row">

                      <th class="c-table__cell c-table__cell--head tag-cell">Number</th>
                      <th class="c-table__cell c-table__cell--head center hidden-xs">Friendly Name</th>
                      <th class="c-table__cell c-table__cell--head center hidden-xs">Country Code</th>
                      <!-- <th class="c-table__cell c-table__cell--head center hidden-xs">Forward Voice To</th> -->
                      <th v-if="isImpersonatingOrDeveloper" class="c-table__cell c-table__cell--head center">Actions</th>

                  </tr>
              </thead>

              <tbody>

                  <tr v-if="loadingCollection">
                      <td class="c-table__cell center" colspan="3">
                          <loading-resource />
                      </td>
                  </tr>

                  <tr v-else-if="loadingCollectionError">
                      <td class="c-table__cell center" colspan="6">
                          <alert-error />
                      </td>
                  </tr>

                  <tr v-else-if="!collection.length">
                      <td class="c-table__cell center" colspan="6">
                          <alert-no-results />
                      </td>
                  </tr>

                  <template v-else>
                      <phone-number-table-row v-for="resource in collection" :phoneNumber="resource" :key="resource.id"></phone-number-table-row>
                  </template>
              </tbody>
          </table>

          <pagination
              v-if="!loadingCollection && !(!prevPageUrl && !nextPageUrl)"
              identity="phone_numbers-pages"
              :totalPageCount="totalPages"
              :nextPage="nextPageUrl"
              :prevPage="prevPageUrl"
          />
      </div>
  </div>
</template>

<script>
import Pagination from '~/components/general/Pagination.vue';
import PhoneNumberForm from '~/components/phone-numbers/PhoneNumberForm.vue';
import PhoneNumberProvision from '~/components/phone-numbers/PhoneNumberProvision.vue';
import PhoneNumberShow from '~/components/phone-numbers/PhoneNumberShow.vue';
import PhoneNumberTableRow from '~/components/phone-numbers/PhoneNumberTableRow.vue';
import Search from '~/components/general/Search.vue';
import AlertNoResults from '../general/AlertNoResults.vue';
import AlertError from '../general/AlertError.vue';
import LoadingResource from '../general/LoadingResource.vue';
import { displayNotActiveModal, isNotActive } from '../../utils/team';
import PageHeader from '~/components/general/PageHeader.vue';
import resourceManagement from './../../mixins/resource-management';
import { VxButtonGroup, VxButton, VxDropdown, VxDropdownItem } from '@voxie/frontend-components';
import {
    faChevronDown,
    faPlus
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import HostPhoneNumberModal from './HostPhoneNumberModal.vue';

export default {
  mixins: [
    resourceManagement
  ],

  components: {
      Pagination,
      PhoneNumberForm,
      PhoneNumberProvision,
      PhoneNumberShow,
      PhoneNumberTableRow,
      Search,
      AlertNoResults,
      AlertError,
      LoadingResource,
      PageHeader,
      VxButtonGroup,
      VxButton,
      VxDropdown,
      VxDropdownItem,
      FontAwesomeIcon,
      HostPhoneNumberModal,
  },

  data() {
      return {
          resourceName: 'phone_numbers',
          resourceReadUrl: `/api/v3/teams/${Spark.state.currentTeam.id}/phone-numbers`,
          resourceWriteUrl: `/api/admin/teams/${Spark.state.currentTeam.id}/phone-numbers`,
          showHostNumberModal: false,
      };
  },

  mounted(){
      Bus.$on('phone_numbers-search-cancel', () => {
          this.loadCollectionAndUi();
      });

      this.listenForResourceEvents();

      const route = this.currentClientRoute();

      if(route && route.hash === 'search') {
          this.loadingCollection = false;
      } else {
          this.loadCollectionAndUi();
      }

      if(isNotActive()) {
          displayNotActiveModal()
      }
  },

  computed: {
      shouldShowList(){
          return !this.editResourceObj && !this.newResourceObj && !this.showResourceObj;
      },

      shouldShowNew(){
          return !this.editResourceObj && this.newResourceObj && !this.showResourceObj;
      },

      shouldShowEdit(){
          return this.editResourceObj && !this.newResourceObj && !this.showResourceObj;
      },

      shouldShowShow(){
          return !this.editResourceObj && !this.newResourceObj && this.showResourceObj;
      },

      searchUrl() {
          const teamId = Spark.state.currentTeam.id;

          return `/api/v3/teams/${teamId}/phone-numbers/filter`;
      },

      isImpersonatingOrDeveloper() {
          return Spark.isImpersonating || Spark.isDeveloper;
      },
      faChevronDown: () => faChevronDown,
      faPlus: () => faPlus,
  },

  methods: {
      /**
       * override method to keep newResourceObj from getting set to null after phone number
       * is added and subsequently closing the phone-provision form when maybe more numbers
       * are desired to be added.  Form must manually be closed.
       */
      resourceCreated(data){
          Bus.$emit(`${this.resourceName}ResourceWasCreated`, data);
          this.collection.unshift(data);
          this.afterResourceUpdated(data);
      },

      presentNewPhoneNumberUi(){
          Bus.$emit('phone_numbersNew');
      },

      onHostNumberSave() {
        this.showHostNumberModal = false;
        this.loadCollectionAndUi();
      }
  }
};
</script>
